import classNames from 'classnames';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { DemoPill } from '@/components/ui/controls';
import { Card, PageHeader } from '@/components/ui/page';
import { StageTag } from '@/components/ui/stage';
import { btn } from '@/components/ui/styles';
import { useIdentity } from '@/features/identity/identity';
import type { StageSuggestion } from '@/pages/leads/suggestion-types';
import type { Message } from '@/pages/messaging/api-types';
import { channelName } from '@/pages/messaging/channels';
import { SegmentList } from '@/pages/messaging/parts';
import { MockSuggesterPill } from '@/pages/leads/suggestions-page';
import type { CasinoList, FollowUpWithLead, LeadFacets, Stage, TelegramChat } from '@/types/api';
import { formatCount, formatMoney, formatRelative, leadTitle, shownEmail } from '@/utils/format';
import { BLOCKED, STAGES } from '@/utils/labels';
import { useApi } from '@/utils/use-api';

const Muted = ({ children }: { children: ReactNode }) => <p className="text-sm text-fg-faint">{children}</p>;

const Skeleton = () => (
  <div className="space-y-2">
    {Array.from({ length: 3 }).map((_, i) => (
      <div key={i} className="h-10 animate-pulse rounded-lg bg-layer" />
    ))}
  </div>
);

/** A card's link to the page that holds the full list; 44px tall on a phone (UI-1). */
const More = ({ to, children }: { to: string; children: ReactNode }) => (
  <Link to={to} className={classNames(btn.link, 'inline-flex min-h-11 items-center whitespace-nowrap md:min-h-0')}>
    {children}
  </Link>
);

/** A card's header extras: they wrap under each other on a narrow screen. */
const Aside = ({ children }: { children: ReactNode }) => <span className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1">{children}</span>;

/** A row that opens its item: hover lifts it with a background, never with weight. At least 44px tall on a phone. */
const RowLink = ({ to, children }: { to: string; children: ReactNode }) => (
  <Link to={to} className="-mx-3 block min-h-11 rounded-lg px-3 py-2.5 transition-colors hover:bg-surface-hover md:min-h-0 md:py-2">
    {children}
  </Link>
);

// Each stage has its own pale background and deep text colour (tokens in src/index.css).
const STAGE_TILE: Record<Stage, string> = {
  new: 'bg-stage-new-bg text-stage-new-fg',
  clarifications: 'bg-stage-clarifications-bg text-stage-clarifications-fg',
  configuring: 'bg-stage-configuring-bg text-stage-configuring-fg',
  paid: 'bg-stage-paid-bg text-stage-paid-fg',
  live: 'bg-stage-live-bg text-stage-live-fg',
};

const MiniStat = ({ label, value, note }: { label: string; value: string; note?: string }) => (
  <div>
    <p className="text-xs text-fg-muted">{label}</p>
    <p className="mt-0.5 text-lg font-semibold text-fg-strong">{value}</p>
    {note && <p className="text-xs text-fg-faint">{note}</p>}
  </div>
);

/**
 * Overview (REM-1, TG-2, CAS-5, LEAD-1…6): what needs the team today, and how the
 * pipeline moves. Lead stages, chats to confirm, due follow-ups, AI suggestions, the
 * latest sent messages (OUT-1…6) and casino totals are real; rows from the local demo
 * seed carry a Demo pill.
 */
const OverviewPage = () => {
  const { me } = useIdentity();
  const facets = useApi<LeadFacets>('/leads/facets');
  const chats = useApi<TelegramChat[]>('/telegram/chats', { status: 'unlinked' });
  const casinos = useApi<CasinoList>('/casinos');
  const followUps = useApi<FollowUpWithLead[]>('/follow-ups');
  const recent = useApi<Message[]>('/messages', { status: 'sent' });
  const recentSent = (recent.data ?? []).slice(0, 3);
  const suggestions = useApi<StageSuggestion[]>('/suggestions', { state: 'open' });
  const toReview = suggestions.data ?? [];

  const now = Date.now();
  const due = (followUps.data ?? []).filter((f) => !f.done_at && new Date(f.due_at).getTime() <= now);
  const stageCount = (stage: string) => facets.data?.stages.find((f) => f.value === stage)?.count ?? 0;
  const totals = casinos.data?.totals;
  const hasMetrics = casinos.data && casinos.data.metrics_source !== 'none';
  const toConfirm = chats.data ?? [];

  return (
    <div>
      <PageHeader title="Overview" description="What needs the team today, and how the pipeline moves." />

      <h2 className="mb-2 text-sm font-medium text-fg-strong">Leads by stage</h2>
      {facets.error && !facets.data && <p className="mb-2 text-sm text-danger">Lead counts could not be loaded.</p>}
      <div className="mb-8 grid auto-rows-fr grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        {[...STAGES.map((s) => ({ key: s.value, to: `/leads?stage=${s.value}`, tile: STAGE_TILE[s.value], count: stageCount(s.value), label: s.label, hint: s.hint })),
          // Blocked is a tag on Configuring or Clarifications, counted apart (Q4).
          { key: 'blocked', to: '/leads?blocked=1', tile: 'bg-stage-blockers-bg text-stage-blockers-fg', count: facets.data?.blocked ?? 0, label: BLOCKED.label, hint: BLOCKED.hint },
        ].map((s) => (
          <Link
            key={s.key}
            to={s.to}
            className={classNames(
              'block h-full rounded-card p-4 shadow-e1 transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand',
              s.tile,
            )}
          >
            <span className="block text-3xl font-semibold tabular-nums">{facets.data ? String(s.count) : '—'}</span>
            <span className="mt-1 block text-sm font-medium">{s.label}</span>
            <span className="mt-1 block text-xs opacity-75">{s.hint}</span>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title={`Chats to confirm${chats.data ? ` (${toConfirm.length})` : ''}`} aside={<More to="/conversations/telegram">Open Conversations</More>}>
          {chats.error && !chats.data ? (
            <p className="text-sm text-danger">The chats could not be loaded.</p>
          ) : !chats.data ? (
            <Skeleton />
          ) : toConfirm.length === 0 ? (
            <Muted>Nothing to confirm. A new Telegram group shows up here until someone confirms who the client is (TG-2).</Muted>
          ) : (
            <ul>
              {toConfirm.slice(0, 5).map((c) => (
                <li key={c.chat_id}>
                  <RowLink to={`/conversations?chat=${c.chat_id}`}>
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="truncate text-sm text-fg-strong">{c.title}</span>
                      {c.last_message_at && <span className="shrink-0 text-xs text-fg-faint">{formatRelative(c.last_message_at, now)}</span>}
                    </div>
                    <p className="truncate text-xs text-fg-muted">{c.claimed_via === 'handle' ? `Telegram handle of ${shownEmail(c.claimed_email) || 'a lead without email'}` : c.claimed_email ? `Says they are ${c.claimed_email}` : 'No email given yet'}</p>
                  </RowLink>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card
          title={`Follow-ups due${followUps.data ? ` (${due.length})` : ''}`}
          aside={
            <More to="/follow-ups">All follow-ups</More>
          }
        >
          {followUps.error && !followUps.data ? (
            <p className="text-sm text-danger">The follow-ups could not be loaded.</p>
          ) : !followUps.data ? (
            <Skeleton />
          ) : due.length === 0 ? (
            <Muted>Nothing due.</Muted>
          ) : (
            <ul>
              {due.map((f) => (
                <li key={f.id}>
                  <RowLink to={`/leads/${encodeURIComponent(f.lead_id)}`}>
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="truncate text-sm text-fg-strong">{leadTitle(f.lead_name, f.lead_email)}</span>
                      <span className="shrink-0 text-xs text-danger">Due {formatRelative(f.due_at, now)}</span>
                    </div>
                    <p className="truncate text-xs text-fg-muted">
                      {f.note || 'No note'} · set by {me && f.created_by_id === me.id ? 'you' : f.created_by || 'unknown'}
                    </p>
                  </RowLink>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card
          notYet="ai"
          title={`AI suggestions to review (${toReview.length})`}
          aside={
            <Aside>
              {toReview.some((s) => s.model === 'mock') && <MockSuggesterPill />}
              <More to="/leads/suggestions">Review</More>
            </Aside>
          }
        >
          {suggestions.error && !suggestions.data ? (
            <Muted>Suggestions could not be loaded.</Muted>
          ) : toReview.length === 0 ? (
            <Muted>Nothing to review. New suggestions arrive once a week.</Muted>
          ) : (
            <ul>
              {toReview.slice(0, 3).map((s) => (
                <li key={s.id}>
                  <RowLink to="/leads/suggestions">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="min-w-0 break-words text-sm text-fg-strong">{leadTitle(s.lead_name, s.lead_email)}</span>
                      <span className="flex items-center gap-1.5">
                        <StageTag stage={s.current_stage} />
                        <span className="text-fg-faint" aria-label="to">
                          →
                        </span>
                        <StageTag stage={s.suggested_stage} />
                      </span>
                    </div>
                    <p className="line-clamp-2 text-xs text-fg-muted">{s.reason}</p>
                  </RowLink>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-2 text-xs text-fg-faint">A person accepts or rejects each one; the AI never changes a stage (CAS-4).</p>
        </Card>

        <Card
          title="Recent messages"
          aside={
            <Aside>
              {recentSent.some((m) => m.demo) && <DemoPill title="Some of these come from the local demo seed">Demo</DemoPill>}
              <More to="/messaging/messages">All messages</More>
            </Aside>
          }
        >
          {recent.error && !recent.data && <Muted>Messages could not be loaded.</Muted>}
          {recent.data && recentSent.length === 0 && <Muted>Nothing sent yet.</Muted>}
          <ul>
            {recentSent.map((m) => (
              <li key={m.id}>
                <RowLink to={`/messaging/messages/${m.id}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="truncate text-sm text-fg-strong">{m.title}</span>
                    {m.sent_at && <span className="shrink-0 text-xs text-fg-faint">{formatRelative(m.sent_at, now)}</span>}
                  </div>
                  <p className="break-words text-xs text-fg-muted">
                    {m.channels.map(channelName).join(', ')} · {m.audience} {m.audience === 1 ? 'recipient' : 'recipients'}
                  </p>
                  {m.segment_ids.length > 0 && (
                    <div className="mt-1.5">
                      <SegmentList ids={m.segment_ids} max={3} />
                    </div>
                  )}
                </RowLink>
              </li>
            ))}
          </ul>
        </Card>

        <Card
          className="lg:col-span-2"
          title="Casinos"
          aside={
            <Aside>
              {casinos.data?.metrics_source === 'mock' && (
                <DemoPill title="GGR, players and velocity are made-up numbers: this is the local mock metrics source.">
                  Demo metrics
                </DemoPill>
              )}
              <More to="/casinos">Open Casinos</More>
            </Aside>
          }
        >
          {casinos.error && !casinos.data ? (
            <p className="text-sm text-danger">The casinos could not be loaded.</p>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <MiniStat label="Casinos" value={totals ? String(totals.casinos) : '—'} note={totals ? `${totals.live} live` : undefined} />
              <MiniStat label="GGR, last 30 days" value={hasMetrics && totals ? formatMoney(totals.ggr_30d_usd) : '—'} note="Live casinos, USD" />
              <MiniStat label="Players, last 30 days" value={hasMetrics && totals ? formatCount(totals.players_30d) : '—'} note="Live casinos, busiest day" />
              <MiniStat label="Average velocity" value={hasMetrics && totals ? totals.avg_velocity.toFixed(1) : '—'} note="Activity score, 0–100" />
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default OverviewPage;
